import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map, switchMap, tap } from 'rxjs/operators';

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';

import { ISnListQueryParameters, ISnListResponse } from '@lib/api';
import { FsSavedSearch } from '@lib/api/entities';
import { SnDialogService } from '@lib/ui/dialog';

@Injectable({ providedIn: 'root' })
export class SnSavedSearchAPI {
  private readonly _cameras$ = new Subject<any[]>();

  private readonly _addNewSentrySearch$ = new Subject<boolean>();

  private readonly _removedSentrySearch$ = new Subject<boolean>();

  private readonly _searchChange$: BehaviorSubject<any> = new BehaviorSubject([]);

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _toastrService: ToastrService,
    private readonly _dialogService: SnDialogService,
    private readonly _router: Router,
  ) {}

  get search(): any {
    return this._searchChange$.value;
  }

  get cameraChange$(): Observable<any[]> {
    return this._cameras$.asObservable();
  }

  get addNewSentrySearch$(): Observable<any> {
    return this._addNewSentrySearch$.asObservable();
  }

  get removeSentrySearch$(): Observable<any> {
    return this._removedSentrySearch$.asObservable();
  }

  public setCameras(cameras: any[]): void {
    this._cameras$.next(cameras);
  }

  public setSearch(search: any): void {
    this._searchChange$.next(search);
  }

  public saveSentrySearch(data: any): Observable<any> {
    return this._httpClient
      .post('/api/saved-searches/', data)
      .pipe(tap(() => this._addNewSentrySearch$.next(true)));
  }

  public deleteSearch(id: string, navigate?: string): Observable<Object> {
    return this._dialogService
      .confirm({
        message: 'common.deleteConfirmation',
        confirmButton: {
          text: 'common.delete',
          color: 'warn',
        },
      })
      .pipe(
        switchMap(() => this._httpClient.delete(`/api/saved-searches/${id}/`)),
        tap({
          next: () => {
            this._removedSentrySearch$.next(true);
            this._toastrService.success('Search deleted');
            if (navigate) {
              this._router.navigate([`/${navigate}`]);
            }
          },
          error: () => this._toastrService.error('Could not delete the search'),
        }),
      );
  }

  public updateActiveAlarm(isActiveAlert: boolean, id: string): Observable<Object> {
    return this._httpClient.patch(`/api/saved-searches/${id}/`, { isActiveAlert }).pipe(
      tap({
        next: () => this._toastrService.success('Search was updated'),
        error: () =>
          this._toastrService.error('Unable to update the search. Please try again later'),
      }),
    );
  }

  public updateSearch(id: string, data: any): Observable<Object> {
    return this._httpClient.patch(`/api/saved-searches/${id}/`, data).pipe(
      tap({
        next: () => this._toastrService.success('Search was updated'),
        error: () =>
          this._toastrService.error('Unable to update the search. Please try again later'),
      }),
    );
  }

  public getList(params?: ISnListQueryParameters): Observable<ISnListResponse<FsSavedSearch>> {
    return this._httpClient.get('/api/saved-searches/', { params }).pipe(
      map((response: any) => ({
        count: response.count,
        totalPages: response.totalPages,
        results: response.results.map((sentry: any) => new FsSavedSearch(sentry)),
      })),
    );
  }

  public getSearchById(id: string): Observable<FsSavedSearch> {
    return this._httpClient
      .get(`/api/saved-searches/${id}/`)
      .pipe(map((reponse) => new FsSavedSearch(reponse)));
  }

  public getSearches(): Observable<any> {
    return this._httpClient.get('/api/saved-searches/');
  }

  public setSearchRules(id: string, data: any): Observable<any> {
    return this._httpClient.put(`/api/saved-searches/${id}/rules/`, data);
  }
}
